import { useState } from 'react';
import { supabase } from './supabaseClient';

const CATEGORIES = [
  { id: 'bug', label: '🐛 Bug Report', color: 'var(--red)', rgb: '--red-rgb' },
  { id: 'feature', label: '✨ Feature Request', color: 'var(--cyan)', rgb: '--cyan-rgb' },
  { id: 'content', label: '📋 Checklist Error', color: 'var(--gold)', rgb: '--gold-rgb' },
  { id: 'other', label: '💬 General Feedback', color: 'var(--green)', rgb: '--green-rgb' },
];

export default function FeedbackWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setLoading(true);
    await supabase.from('feedback').insert({
      user_id: user?.id || null,
      email: user?.email || null,
      category,
      message: message.trim(),
      page: window.location.pathname,
      created_at: new Date().toISOString(),
    });
    setLoading(false);
    setDone(true);
    setTimeout(() => {
      setDone(false);
      setOpen(false);
      setMessage('');
      setCategory('bug');
    }, 2500);
  };

  return (
    <>
      {/* Floating button */}
      <button
        style={{ ...styles.fab, ...(open ? styles.fabOpen : {}) }}
        onClick={() => setOpen(o => !o)}
        title="Send feedback"
      >
        {open ? '✕' : '💬'}
      </button>

      {/* Panel */}
      {open && (
        <div style={styles.panel}>
          {done ? (
            <div style={styles.successState}>
              <div style={styles.successIcon}>✓</div>
              <div style={styles.successText}>Thanks for the feedback!</div>
              <div style={styles.successSub}>We'll take a look.</div>
            </div>
          ) : (
            <>
              <div style={styles.panelHeader}>
                <span style={styles.panelTitle}>SEND FEEDBACK</span>
                <span style={styles.panelSub}>Help us improve CollectionKeeper</span>
              </div>

              <div style={styles.categories}>
                {CATEGORIES.map(c => (
                  <button
                    key={c.id}
                    style={{
                      ...styles.catBtn,
                      ...(category === c.id ? { borderColor: c.color, color: c.color, background: `rgba(var(${c.rgb}),0.08)` } : {})
                    }}
                    onClick={() => setCategory(c.id)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <textarea
                style={styles.textarea}
                placeholder={
                  category === 'bug' ? 'Describe what happened and what you expected...' :
                  category === 'feature' ? 'What would you like to see added or changed?' :
                  category === 'content' ? 'Which set and card number has an error?' :
                  'What\'s on your mind?'
                }
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
              />

              <div style={styles.panelFooter}>
                {user?.email && (
                  <span style={styles.fromLabel}>from {user.email}</span>
                )}
                <button
                  style={{ ...styles.submitBtn, ...(message.trim() ? {} : styles.submitBtnDisabled) }}
                  onClick={handleSubmit}
                  disabled={loading || !message.trim()}
                >
                  {loading ? 'SENDING...' : 'SEND →'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

const styles = {
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'var(--panel-deep)',
    border: '1px solid var(--border)',
    color: 'var(--muted)',
    fontSize: '20px',
    cursor: 'pointer',
    zIndex: 9998,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    transition: 'border-color 0.2s, color 0.2s',
  },
  fabOpen: {
    borderColor: 'var(--cyan)',
    color: 'var(--cyan)',
    fontSize: '16px',
  },
  panel: {
    position: 'fixed',
    bottom: '84px',
    right: '24px',
    width: '320px',
    background: 'var(--panel-deep)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
    zIndex: 9997,
    overflow: 'hidden',
  },
  panelHeader: {
    padding: '16px 16px 12px',
    borderBottom: '1px solid var(--border)',
  },
  panelTitle: {
    display: 'block',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    color: 'var(--cyan)',
    marginBottom: '4px',
  },
  panelSub: {
    display: 'block',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--muted)',
  },
  categories: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px',
    padding: '12px 16px',
  },
  catBtn: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '10px',
    padding: '7px 8px',
    background: 'transparent',
    border: '1px solid var(--border)',
    color: 'var(--muted)',
    cursor: 'pointer',
    borderRadius: '2px',
    textAlign: 'left',
    transition: 'all 0.15s',
  },
  textarea: {
    width: '100%',
    background: 'var(--panel)',
    border: 'none',
    borderTop: '1px solid var(--border)',
    borderBottom: '1px solid var(--border)',
    color: 'var(--text)',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '12px',
    padding: '12px 16px',
    resize: 'none',
    outline: 'none',
    boxSizing: 'border-box',
    lineHeight: 1.6,
  },
  panelFooter: {
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  fromLabel: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '10px',
    color: 'var(--muted-2)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  submitBtn: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '10px',
    letterSpacing: '1px',
    padding: '8px 16px',
    background: 'rgba(var(--cyan-rgb),0.1)',
    border: '1px solid var(--cyan)',
    color: 'var(--cyan)',
    cursor: 'pointer',
    borderRadius: '2px',
    flexShrink: 0,
  },
  submitBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  successState: {
    padding: '32px 16px',
    textAlign: 'center',
  },
  successIcon: {
    fontSize: '32px',
    color: 'var(--green)',
    marginBottom: '8px',
  },
  successText: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '13px',
    letterSpacing: '2px',
    color: 'var(--green)',
    marginBottom: '4px',
  },
  successSub: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--muted)',
  },
};
